import { axiosClient } from "./axiosClient";

// ── Ściana społecznościowa grupy (4B) ──
export type WallAuthor = {
  id: string;
  name: string;
  avatar_url?: string | null;
  profile_ref?: string | null;
  verified?: boolean;
};

export type WallComment = {
  id: string;
  body: string;
  created_at: string;
  parent_id?: string | null;
  author: WallAuthor;
  can_delete: boolean;
  can_report?: boolean;
  replies?: WallComment[];
};

export type Mentionable = {
  name: string;
  profile_ref: string;
  avatar_url?: string | null;
};

export type WallResultAnswer = { content: string; count: number; percent: number };
export type WallResultQuestion = {
  content: string;
  qtype: string;
  total_votes: number;
  outcome?: { kind: string; winner?: string; yes_percent?: number; passed?: string[]; [k: string]: unknown } | null;
  answers: WallResultAnswer[];
};
export type WallVotingResults = {
  turnout_percent?: number | null;
  voted_count: number;
  eligible_count: number;
  valid: boolean;
  quorum_percent?: number | null;
  questions: WallResultQuestion[];
};

export type WallVotingPreview = {
  id: string;
  title: string;
  status: string;
  public?: boolean;
  start_date?: string | null;
  end_date?: string | null;
  questions_count?: number;
  voters_count?: number;
  results?: WallVotingResults | null; // tylko gdy zakończone i widz uprawniony
  results_locked?: boolean; // zakończone, ale wyniki niedostępne dla tego widza
};

export type WallPost = {
  id: string;
  kind?: "text" | "voting" | "portal";
  body: string;
  voting?: WallVotingPreview | null;
  pinned: boolean;
  created_at: string;
  author: WallAuthor;
  reactions_count: number;
  comments_count: number;
  reacted_by_me: boolean;
  can_delete: boolean;
  can_report?: boolean;
  comments: WallComment[];
  // Posty portalowe (kind === "portal"): polityka komentowania + czy widz może komentować.
  comments_policy?: "all" | "verified" | "none";
  can_comment?: boolean;
  // Kontekst tematu — ustawiony, gdy post pochodzi z dyskusji (np. w Aktualnościach pod przełącznikiem).
  topic?: { id: string; title: string } | null;
};

export type WallMemberPreview = {
  name: string;
  initials: string;
  avatar_url?: string | null;
  profile_ref?: string | null;
};

export type WallVoting = {
  id: string;
  title: string;
  status: string;
  start_date?: string | null;
  end_date?: string | null;
};

export type WallHeader = {
  id: string;
  name: string;
  description?: string | null;
  avatar_url?: string | null;
  visibility?: "private" | "public";
  created_at?: string;
  members_count: number;
  who_can_post: "all" | "verified" | "owner";
  who_can_create_votings?: "owner" | "moderators" | "members";
  is_owner: boolean;
  is_moderator: boolean;
  can_post: boolean;
  can_create_voting?: boolean;
  members_preview: WallMemberPreview[];
  votings: WallVoting[];
  me: { name: string; avatar_url?: string | null };
  signup_enabled?: boolean;
  signup_token?: string | null;
  signup_closes_at?: string | null;
};

export type WallResponse = { group?: WallHeader; posts: WallPost[]; next_cursor?: string | null; has_more: boolean };

// Pierwsza strona: bez cursor (zwraca też `group`). Kolejne: z cursor (lazy/infinite).
// includeTopics → dokleja też posty z dyskusji (zakładka „Tematy") z kontekstem tematu na karcie.
export const getGroupWall = (groupId: string, cursor?: string | null, includeTopics?: boolean) =>
  axiosClient.get<WallResponse>(`voter_groups/${groupId}/wall`, {
    params: { ...(cursor ? { cursor } : {}), ...(includeTopics ? { include_topics: true } : {}) }
  });

export const getMentionable = (groupId: string, q: string) =>
  axiosClient.get<{ results: Mentionable[] }>(`voter_groups/${groupId}/wall/mentionable`, { params: { q } });

// Permalink pojedynczego posta (deep-link z powiadomień) — otwiera dokładnie ten post + komentarze,
// z tym SAMYM kontekstem grupy (opis/członkowie/głosowania) co hub.
export type WallPostDetail = {
  group: WallHeader;
  post: WallPost;
};
export const getGroupPost = (groupId: string, postId: string) =>
  axiosClient.get<WallPostDetail>(`voter_groups/${groupId}/posts/${postId}`);

export const createWallPost = (groupId: string, body: string) =>
  axiosClient.post<WallPost>(`voter_groups/${groupId}/posts`, { body });

export const deleteWallPost = (groupId: string, postId: string) =>
  axiosClient.delete(`voter_groups/${groupId}/posts/${postId}`);

export const pinWallPost = (groupId: string, postId: string) =>
  axiosClient.post<WallPost>(`voter_groups/${groupId}/posts/${postId}/pin`);

export const reactWallPost = (groupId: string, postId: string) =>
  axiosClient.post<{ reacted: boolean; reactions_count: number }>(
    `voter_groups/${groupId}/posts/${postId}/react`
  );

export const createWallComment = (groupId: string, postId: string, body: string, parentId?: string) =>
  axiosClient.post<WallComment>(`voter_groups/${groupId}/posts/${postId}/comments`, { body, parent_id: parentId });

export const deleteWallComment = (groupId: string, commentId: string) =>
  axiosClient.delete(`voter_groups/${groupId}/comments/${commentId}`);

// ── Zagregowany, w pełni interaktywny feed (ekran GRUPY → Feed) ──
// Posty to pełne WallPost (komentarze/reakcje/odpowiedzi) + kontekst grupy.
export type FeedGroupRef = {
  id: string;
  name: string;
  avatar_url?: string | null;
  is_public?: boolean;
  is_member?: boolean;       // czy widz jest członkiem (steruje gatingiem interakcji)
  requires_approval?: boolean;
};
// can_interact = członek (może komentować/reagować); nie-członek widzi „Dołącz, żeby odpowiadać".
// group jest opcjonalny — posty portalowe (kind === "portal") nie mają kontekstu grupy.
export type FeedPost = WallPost & { group?: FeedGroupRef; can_interact?: boolean };

export type FeedSidebarGroup = {
  id: string;
  name: string;
  avatar_url?: string | null;
  role: "owner" | "member";
  members_count: number;
};

export type FeedSidebarVoting = {
  id: string;
  title: string;
  status: string;
  start_date?: string | null;
  end_date?: string | null;
};

export type WallFeedResponse = {
  posts: FeedPost[];
  next_cursor?: string | null;
  has_more: boolean;
  sidebar?: { my_groups: FeedSidebarGroup[]; active_votings: FeedSidebarVoting[] };
};

// scope: "groups" → tylko moje grupy (feed sekcji Grupy); domyślnie pełna Społeczność (grupy + obserwowani + portal).
// Pierwsza strona bez cursor (zwraca też `sidebar`). Kolejne z cursor (infinite-scroll).
export const getGroupsWallFeed = (
  cursor?: string | null,
  scope?: "groups" | "community" | "following",
  opts?: { includeGroups?: boolean; includeTopics?: boolean }
) =>
  axiosClient.get<WallFeedResponse>("voter_groups/wall_feed", {
    params: {
      ...(cursor ? { cursor } : {}),
      ...(scope ? { scope } : {}),
      // „Grupy" domyślnie ON (wysyłamy tylko gdy wyłączone); „Tematy" tylko gdy włączone.
      ...(opts?.includeGroups === false ? { include_groups: false } : {}),
      ...(opts?.includeTopics ? { include_topics: true } : {})
    }
  });
